import {Component, EventEmitter, Input, OnChanges, Output} from "@angular/core";
import {createEvent} from "../misc/event.polyfill";
import {UploaderDialogResult} from "../uploader-wrapper/uploader-dialog.model";
import {
  SandyAssetModel,
  SandyImportDataModel,
  SandyLineItemModel,
  SandyPreImportDataModel,
  sandyPreImportDataModelFactory,
} from "./sandy-import-data.model";
import {SearchHelpers} from "../helpers/search-helper";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {SandyAssociateDialogComponent} from "./sandy-associate-dialog.component";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SandyService} from "./sandy.service";
import {toggleLoading} from "../misc/loading.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "sandy-import",
  templateUrl: "./sandy-import.template.pug",
  providers: [SandyService],
})
export class SandyImportComponent implements OnChanges {
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  searchText: string = "";
  importParams: {[key: string]: string} = {};

  sandyImportResponseProcessor = SandyImportDataModel.process;

  sandyItems: SandyLineItemModel[] = [];

  assets: SandyAssetModel[] = [];
  filteredAssets: SandyAssetModel[] = [];
  displayedAssets: SandyAssetModel[] = [];
  sandyPreImportData: SandyPreImportDataModel = sandyPreImportDataModelFactory();

  loading: boolean = true;
  showMatcher: boolean = false;

  /**
   * Error used by handleErrors()
   * Don't remove these
   */
  private get error() {
    return undefined;
  }

  private set error(error: Error) {
    this.emitError(error);
  }

  @Input()
  entityId: string;

  @Input()
  entityParam: "projectId" | "siteId" | "orderId";

  @Output("error")
  errorEmitter: EventEmitter<Event> = new EventEmitter<Event>();

  constructor(
    private dialogService: DialogService,
    private sandyService: SandyService,
  ) {}

  ngOnChanges() {
    if (!this.entityParam || !this.entityId) return;

    this.importParams = {[`${this.entityParam}`]: this.entityId};
    this.loadStatus();
  }

  @toggleLoading()
  @handleErrors()
  private async loadStatus() {
    this.sandyPreImportData = await this.sandyService.getPreImportStatus(this.importParams);
  }

  private emitError(error?: Error) {
    const event = createEvent("error");
    event["error"] = error;
    this.errorEmitter.emit(event);
  }

  private updateDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.filteredAssets = this.assets.filter((asset) => {
      if (!asset || !asset.eligibleSandyItems.length) return false; // if no selection options, don't display

      if (this.searchText) {
        const values = [
          asset.assetId,
          asset.productNumber,
          asset.productDescription,
          asset.deviceKey,
          asset.orderDate,
          asset.orderNumber,
          asset.readableType,
          asset.serialNumber,
          asset.site,
          asset.building,
          asset.floor,
          asset.mapId,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedAssets = this.filteredAssets.slice(start, end);

    this.recalculateMaxPages();
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filteredAssets.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        break;
    }

    this.updateDisplay();
  }

  fileUploaded(event: Event) {
    this.emitError();

    const value = (event["value"] || {}) as UploaderDialogResult;
    const data = SandyImportDataModel.merge((value["responses"] || []) as SandyImportDataModel[]);

    this.assets = data.assets;
    this.sandyItems = data.sandyItems;

    this.showMatcher = !!(this.assets.length && this.sandyItems.length);
    this.updateDisplay();
  }

  @handleErrors()
  async changeSerialNumber(asset: SandyAssetModel) {
    await SandyAssociateDialogComponent.show(this.dialogService, asset);
  }

  @toggleLoading()
  @handleErrors()
  async fetch() {
    const data = await this.sandyService.fetch(this.importParams);

    this.assets = data.assets;
    this.sandyItems = data.sandyItems;

    this.showMatcher = !!(this.assets.length && this.sandyItems.length);
    this.updateDisplay();
  }

  autoAssignAll() {
    for (const asset of this.filteredAssets) {
      asset.autoAssignSandyItem();
    }
  }

  @toggleLoading()
  @handleErrors()
  async save() {
    this.sandyPreImportData = await this.sandyService.save(this.assets, this.sandyItems, this.importParams);

    this.assets = [];
    this.sandyItems = [];
    this.showMatcher = false;

    this.updateDisplay();
  }
}
